import {
  collection,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { CHANGE_REQUESTS_COLLECTION } from '../types/changeRadar';

/**
 * Root-Felder inkl. nested clientData.* sicher patchen.
 * setDoc({ merge }) behandelt Keys mit Punkt als Literalfeldnamen — daher updateDoc.
 */
export async function patchKalkulationMandantRoot(
  uid: string,
  fields: Record<string, unknown>
): Promise<void> {
  const ref = doc(db, 'kalkulation_mandanten', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, fields as Parameters<typeof updateDoc>[1]);
    return;
  }
  const nested: Record<string, any> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (!key.includes('.')) {
      nested[key] = value;
      continue;
    }
    const parts = key.split('.');
    let cur = nested;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      cur[p] = cur[p] && typeof cur[p] === 'object' ? cur[p] : {};
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = value;
  }
  await setDoc(ref, nested, { merge: true });
}

export type PremiumBillingMode = 'yearly' | 'monthly';

export interface RegisterPremiumToolBookingParams {
  toolId: string;
  toolName: string;
  /** Listenpreis (Jahresgesamtbetrag bei yearly, Monatspreis bei monthly) */
  price: number;
  billingMode: PremiumBillingMode;
  /** UI/Akte: monthly = Abo, yearly = Einmalzahlung 12 Monate */
  planType?: 'monthly' | 'yearly';
}

/** Empfohlene Listenpreise für Premium-Tools (Unlock ohne Plan-Auswahl). */
export const PREMIUM_TOOL_BOOKING_CATALOG: Record<
  string,
  { toolName: string; price: number; billingMode: PremiumBillingMode }
> = {
  'pe-start': { toolName: 'PEStart', price: 46, billingMode: 'monthly' },
  'pe-profit': { toolName: 'PEProfit', price: 55, billingMode: 'monthly' },
  'pe-papers': { toolName: 'PEPapers', price: 19.99, billingMode: 'monthly' },
  'pe-rent': { toolName: 'PERent', price: 14.99, billingMode: 'monthly' },
  'pe-scout': { toolName: 'PEScout', price: 29.99, billingMode: 'monthly' },
  'pe-facts': { toolName: 'PEFacts', price: 11.99, billingMode: 'monthly' },
  'pe-plan': { toolName: 'PEPlan', price: 24.99, billingMode: 'monthly' },
};

/** ~12 Monate Mindestlaufzeit vor ordentlicher Kündigung */
export const PREMIUM_MIN_TERM_MS = 365 * 24 * 60 * 60 * 1000;

export const isPremiumCancelAllowed = (activatedAt?: string | null): boolean => {
  if (!activatedAt) return false;
  const start = Date.parse(activatedAt);
  if (!Number.isFinite(start)) return false;
  return Date.now() - start >= PREMIUM_MIN_TERM_MS;
};

export const formatPremiumCancelAvailableDate = (activatedAt?: string | null): string => {
  if (!activatedAt) return '—';
  const start = Date.parse(activatedAt);
  if (!Number.isFinite(start)) return '—';
  const available = new Date(start + PREMIUM_MIN_TERM_MS);
  return available.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/** Ende der 12-Monats-Mindestlaufzeit (= erstes Kündigungsdatum) */
export const formatPremiumTermEndDate = formatPremiumCancelAvailableDate;

export const formatPremiumActivatedDate = (activatedAt?: string | null): string => {
  if (!activatedAt) return '—';
  const start = Date.parse(activatedAt);
  if (!Number.isFinite(start)) return '—';
  return new Date(start).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const resolvePERentBooking = (
  licenseType: 'single_300' | 'sub_24'
): RegisterPremiumToolBookingParams => {
  if (licenseType === 'sub_24') {
    return {
      toolId: 'pe-rent',
      toolName: 'PERent',
      price: 14.99,
      billingMode: 'monthly',
      planType: 'monthly',
    };
  }
  return {
    toolId: 'pe-rent',
    toolName: 'PERent',
    price: 199,
    billingMode: 'yearly',
    planType: 'yearly',
  };
};

export const resolvePEPapersBooking = (
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams => {
  if (plan === 'yearly') {
    return {
      toolId: 'pe-papers',
      toolName: 'PEPapers',
      price: 260,
      billingMode: 'yearly',
      planType: 'yearly',
    };
  }
  return {
    toolId: 'pe-papers',
    toolName: 'PEPapers',
    price: 19.99,
    billingMode: 'monthly',
    planType: 'monthly',
  };
};

export const resolvePEStartBooking = (
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams => {
  if (plan === 'yearly') {
    return {
      toolId: 'pe-start',
      toolName: 'PEStart',
      price: 549,
      billingMode: 'yearly',
      planType: 'yearly',
    };
  }
  return {
    toolId: 'pe-start',
    toolName: 'PEStart',
    price: 46,
    billingMode: 'monthly',
    planType: 'monthly',
  };
};

export const resolvePEProfitBooking = (
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams => {
  if (plan === 'yearly') {
    return {
      toolId: 'pe-profit',
      toolName: 'PEProfit',
      price: 659,
      billingMode: 'yearly',
      planType: 'yearly',
    };
  }
  return {
    toolId: 'pe-profit',
    toolName: 'PEProfit',
    price: 55,
    billingMode: 'monthly',
    planType: 'monthly',
  };
};

export const resolvePEScoutBooking = (
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams => {
  if (plan === 'yearly') {
    return {
      toolId: 'pe-scout',
      toolName: 'PEScout',
      price: 380,
      billingMode: 'yearly',
      planType: 'yearly',
    };
  }
  return {
    toolId: 'pe-scout',
    toolName: 'PEScout',
    price: 29.99,
    billingMode: 'monthly',
    planType: 'monthly',
  };
};

export const resolvePEPlanBooking = (
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams => {
  if (plan === 'yearly') {
    return {
      toolId: 'pe-plan',
      toolName: 'PEPlan',
      price: 299.0,
      billingMode: 'yearly',
      planType: 'yearly',
    };
  }
  return {
    toolId: 'pe-plan',
    toolName: 'PEPlan',
    price: 24.99,
    billingMode: 'monthly',
    planType: 'monthly',
  };
};

export const resolvePEFactsBooking = (
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams => {
  if (plan === 'yearly') {
    return {
      toolId: 'pe-facts',
      toolName: 'PEFacts',
      price: 189.0,
      billingMode: 'yearly',
      planType: 'yearly',
    };
  }
  return {
    toolId: 'pe-facts',
    toolName: 'PEFacts',
    price: 11.99,
    billingMode: 'monthly',
    planType: 'monthly',
  };
};

export const resolvePremiumDualBooking = (
  toolId: string,
  plan: 'monthly' | 'yearly'
): RegisterPremiumToolBookingParams | null => {
  switch (toolId) {
    case 'pe-papers':
      return resolvePEPapersBooking(plan);
    case 'pe-start':
      return resolvePEStartBooking(plan);
    case 'pe-profit':
      return resolvePEProfitBooking(plan);
    case 'pe-scout':
      return resolvePEScoutBooking(plan);
    case 'pe-facts':
      return resolvePEFactsBooking(plan);
    case 'pe-plan':
      return resolvePEPlanBooking(plan);
    case 'pe-rent':
      return plan === 'yearly'
        ? resolvePERentBooking('single_300')
        : resolvePERentBooking('sub_24');
    default:
      return null;
  }
};

/**
 * Solange false: Premium-Tools erscheinen auf der Vertragsübersicht mit Sollpreis,
 * werden aber wie Pro Bono mit 0 € abgerechnet (kein Stripe-/Vertrags-Aufpreis).
 * Erst mit Stripe-Anbindung auf true setzen.
 */
export const PREMIUM_CONTRACT_BILLING_ENABLED = false;

export type PremiumContractLine = {
  toolId: string;
  toolName: string;
  planType: 'monthly' | 'yearly';
  listPrice: number;
  /** Monatsäquivalent für Summen (yearly ≈ price/12) */
  monthlyEquivalent: number;
  planLabel: string;
  activatedAt?: string | null;
};

/** Freigeschaltete Premium-Tools → Abrechnungszeilen für die Vertragsübersicht. */
export function buildPremiumContractLines(
  unlockedIds: unknown,
  planTypeMap?: Record<string, string> | null,
  activatedAtMap?: Record<string, string> | null
): PremiumContractLine[] {
  const ids = Array.isArray(unlockedIds)
    ? unlockedIds.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
    : [];
  const plans =
    planTypeMap && typeof planTypeMap === 'object' ? planTypeMap : ({} as Record<string, string>);
  const activated =
    activatedAtMap && typeof activatedAtMap === 'object'
      ? activatedAtMap
      : ({} as Record<string, string>);

  const lines: PremiumContractLine[] = [];
  for (const toolId of ids) {
    const rawPlan = String(plans[toolId] || 'monthly').toLowerCase();
    const planType: 'monthly' | 'yearly' =
      rawPlan === 'yearly' || rawPlan === 'single_300' ? 'yearly' : 'monthly';
    const booking = resolvePremiumDualBooking(toolId, planType);
    const fallback = PREMIUM_TOOL_BOOKING_CATALOG[toolId];
    const toolName = booking?.toolName || fallback?.toolName || toolId;
    const listPrice = Number(booking?.price ?? fallback?.price ?? 0) || 0;
    const monthlyEquivalent =
      planType === 'yearly' ? Math.round((listPrice / 12) * 100) / 100 : listPrice;
    lines.push({
      toolId,
      toolName,
      planType,
      listPrice,
      monthlyEquivalent,
      planLabel: planType === 'yearly' ? 'Einmalzahlung / Jahr' : 'Monatsabo',
      activatedAt: activated[toolId] || null,
    });
  }
  return lines.sort((a, b) => a.toolName.localeCompare(b.toolName, 'de'));
}

export type PremiumToolOwnerBilling = {
  /** Monatsäquivalent der Listenpreise */
  sollMonthly: number;
  /** Tatsächlich abgerechneter Betrag. 0 solange Pro Bono / Stripe noch aus. */
  billedMonthly: number;
  /** true = noch nicht kostenpflichtig (Pro Bono oder Stripe-Schalter aus) */
  proBono: boolean;
  /** Premium-Tool-Umsatz wird nie mit Coaches geteilt. */
  ownerOnly: true;
};

/**
 * Premium-Tools in der Abrechnung: Soll sichtbar, Einnahme nur beim Inhaber.
 * Coach-Provision rechnet ausschließlich den Coaching-Monatsbeitrag.
 */
export function resolvePremiumToolOwnerBilling(client: any): PremiumToolOwnerBilling {
  const cd = client?.clientData || {};
  const ids = client?.unlockedPremiumTools || cd.unlockedPremiumTools || [];
  const plans = client?.premiumToolPlanType || cd.premiumToolPlanType || null;
  const activated = client?.premiumToolActivatedAt || cd.premiumToolActivatedAt || null;
  const lines = buildPremiumContractLines(ids, plans, activated);
  const sollMonthly =
    Math.round(lines.reduce((sum, line) => sum + (Number(line.monthlyEquivalent) || 0), 0) * 100) /
    100;
  const isProBono = !!(
    client?.proBonoActive ||
    cd.proBonoActive ||
    client?.proBonoChecked ||
    cd.proBonoChecked
  );
  const proBono = isProBono || !PREMIUM_CONTRACT_BILLING_ENABLED;
  return {
    sollMonthly,
    billedMonthly: proBono ? 0 : sollMonthly,
    proBono,
    ownerOnly: true,
  };
}

const toIsoDate = (raw: unknown): string | null => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const t = Date.parse(raw);
    return Number.isFinite(t) ? new Date(t).toISOString() : null;
  }
  if (typeof (raw as { toDate?: () => Date }).toDate === 'function') {
    try {
      return (raw as { toDate: () => Date }).toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && raw !== null && 'seconds' in (raw as object)) {
    const sec = Number((raw as { seconds?: number }).seconds);
    if (Number.isFinite(sec)) return new Date(sec * 1000).toISOString();
  }
  return null;
};

const earliestIso = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(a) <= Date.parse(b) ? a : b;
};

/**
 * Echte Freischalt-Daten aus Firebase — nie „heute“ erfinden.
 * Quellen: nested Map, alte Literalfelder (kaputter setDoc), Change-Radar-Tickets.
 */
export async function resolvePremiumActivationDatesFromFirebase(
  uid: string,
  toolIds: string[]
): Promise<Record<string, string>> {
  const found: Record<string, string> = {};
  if (!uid || !toolIds.length) return found;

  const rootSnap = await getDoc(doc(db, 'kalkulation_mandanten', uid));
  const root = rootSnap.exists() ? (rootSnap.data() as Record<string, any>) : {};
  const nestedMap =
    root?.clientData?.premiumToolActivatedAt &&
    typeof root.clientData.premiumToolActivatedAt === 'object'
      ? (root.clientData.premiumToolActivatedAt as Record<string, unknown>)
      : {};

  for (const toolId of toolIds) {
    let best: string | null = toIsoDate(nestedMap[toolId]);
    const literalKey = `clientData.premiumToolActivatedAt.${toolId}`;
    best = earliestIso(best, toIsoDate(root[literalKey]));
    if (best) found[toolId] = best;
  }

  try {
    const ticketsSnap = await getDocs(
      collection(db, 'kalkulation_mandanten', uid, CHANGE_REQUESTS_COLLECTION)
    );
    ticketsSnap.forEach((ticketDoc) => {
      const data = ticketDoc.data() as Record<string, any>;
      const toolId = String(data.toolId || '');
      if (!toolIds.includes(toolId)) return;
      if (String(data.category || '') === 'Abo-Kündigung') return;
      const iso =
        toIsoDate(data.createdAt) ||
        toIsoDate(data.createdAtServer) ||
        toIsoDate(data.timestamp);
      if (!iso) return;
      found[toolId] = earliestIso(found[toolId] || null, iso) || iso;
    });
  } catch (err) {
    console.warn('Change-Radar-Daten für Premium-Laufzeit nicht lesbar:', err);
  }

  return found;
}

/**
 * Fehlende Freischalt-Daten aus Firebase-Historie wiederherstellen.
 * Ohne Nachweis wird KEIN Tagesdatum gesetzt.
 */
export const repairMissingPremiumActivationDates = async (): Promise<boolean> => {
  try {
    const useAppStore = (await import('../store/index')).default;
    const useAdminStore = (await import('../store/adminStore')).default;
    const state = useAppStore.getState();
    const unlocked = Array.isArray(state.unlockedPremiumTools)
      ? state.unlockedPremiumTools.filter(
          (id): id is string => typeof id === 'string' && id.length > 0
        )
      : [];
    if (!unlocked.length) return false;

    const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
    if (!uid) return false;

    const recovered = await resolvePremiumActivationDatesFromFirebase(uid, unlocked);

    const prevActivated =
      state.clientData?.premiumToolActivatedAt &&
      typeof state.clientData.premiumToolActivatedAt === 'object'
        ? { ...(state.clientData.premiumToolActivatedAt as Record<string, string>) }
        : {};
    const prevPlans =
      state.clientData?.premiumToolPlanType &&
      typeof state.clientData.premiumToolPlanType === 'object'
        ? { ...(state.clientData.premiumToolPlanType as Record<string, string>) }
        : {};

    const nextActivated = { ...prevActivated };
    const nextPlans = { ...prevPlans };
    let changed = false;

    for (const toolId of unlocked) {
      const real = recovered[toolId];
      const current = nextActivated[toolId];
      // Fehlend → echte Historie; oder jüngeres Fake-Datum durch älteren Nachweis ersetzen.
      // Falls das Tool frisch freigeschaltet wurde und noch gar kein Datum existiert, jetzt setzen!
      if (real && (!current || Date.parse(real) < Date.parse(current))) {
        nextActivated[toolId] = real;
        changed = true;
      } else if (!current) {
        nextActivated[toolId] = new Date().toISOString();
        changed = true;
      }
      if (nextPlans[toolId] !== 'monthly' && nextPlans[toolId] !== 'yearly') {
        nextPlans[toolId] = 'monthly';
        changed = true;
      }
    }
    if (!changed) return false;

    if (typeof state.setClientData === 'function') {
      state.setClientData((prev: any) => ({
        ...(prev || {}),
        unlockedPremiumTools: unlocked,
        premiumToolActivatedAt: nextActivated,
        premiumToolPlanType: nextPlans,
      }));
    } else {
      useAppStore.setState({
        clientData: state.clientData
          ? {
              ...state.clientData,
              unlockedPremiumTools: unlocked,
              premiumToolActivatedAt: nextActivated,
              premiumToolPlanType: nextPlans,
            }
          : state.clientData,
        isDirty: true,
      });
    }

    await patchKalkulationMandantRoot(uid, {
      unlockedPremiumTools: unlocked,
      'clientData.unlockedPremiumTools': unlocked,
      'clientData.premiumToolActivatedAt': nextActivated,
      'clientData.premiumToolPlanType': nextPlans,
    });
    return true;
  } catch (err) {
    console.error('repairMissingPremiumActivationDates fehlgeschlagen:', err);
    return false;
  }
};

/**
 * Setzt alle Premium-Freischaltungen + Kostenbuchungen für den aktuellen Mandanten zurück
 * (Test/Neubuchung mit korrekten Laufzeit-Daten).
 */
export const resetAllPremiumToolBookings = async (): Promise<void> => {
  try {
    const useAppStore = (await import('../store/index')).default;
    const useAdminStore = (await import('../store/adminStore')).default;
    const state = useAppStore.getState();
    const prev = state.clientData;

    useAppStore.setState({
      unlockedPremiumTools: [],
      isDirty: true,
      clientData: prev
        ? {
            ...prev,
            bwaData: {
              ...(prev.bwaData || {}),
              premiumSoftware: 0,
            },
            unlockedPremiumTools: [],
            premiumToolActivatedAt: {},
            premiumToolPlanType: {},
          }
        : prev,
    });

    try {
      const { usePERentStore } = await import('../store/usePERentStore');
      usePERentStore.setState({ isLicensed: false, licenseType: null });
    } catch {
      /* ignore */
    }

    const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
    if (!uid) return;

    await patchKalkulationMandantRoot(uid, {
      unlockedPremiumTools: [],
      'clientData.unlockedPremiumTools': [],
      'clientData.bwaData.premiumSoftware': 0,
      'clientData.premiumToolActivatedAt': {},
      'clientData.premiumToolPlanType': {},
    });
  } catch (err) {
    console.error('resetAllPremiumToolBookings fehlgeschlagen:', err);
    throw err;
  }
};

export const calcMonthlyPremiumCost = (
  price: number,
  billingMode: PremiumBillingMode
): number => {
  const safe = Number(price) || 0;
  if (billingMode === 'monthly') return Number(safe.toFixed(2));
  return Number((safe / 12).toFixed(2));
};

const writeChangeRadarTicket = async (opts: {
  uid: string;
  clientData: Record<string, any>;
  title: string;
  message: string;
  toolId: string;
  toolName: string;
  monthlyCost: number;
  category: string;
}) => {
  const firmenname = String(opts.clientData.firmenname || opts.clientData.salonname || 'Mandant');
  const mandantNr = String(opts.clientData.mandantNr || '');
  const betreuer = String(opts.clientData.betreuer || '');
  const senderName =
    String(opts.clientData.unternehmername || '').trim() || firmenname;
  const senderEmail = String(opts.clientData.mailadresse || '').trim();

  await addDoc(collection(db, 'kalkulation_mandanten', opts.uid, CHANGE_REQUESTS_COLLECTION), {
    clientId: opts.uid,
    mandantNr,
    firmenname,
    betreuer,
    status: 'pending',
    unlock_h2: false,
    type: 'change_request',
    title: opts.title,
    message: opts.message,
    category: opts.category,
    toolId: opts.toolId,
    toolName: opts.toolName,
    monthlyCost: opts.monthlyCost,
    senderName,
    senderEmail,
    coachName: betreuer,
    createdAt: new Date().toISOString(),
    createdAtServer: serverTimestamp(),
  });
};

/**
 * Bucht Tool-Lizenzkosten in BWA (`premiumSoftware`, Jahresbasis) und legt
 * ein Change-Radar-Ticket (pending) für den Coach an.
 */
export const registerPremiumToolBooking = async (
  params: RegisterPremiumToolBookingParams
): Promise<{ monthlyCost: number; yearlyDelta: number } | null> => {
  const monthlyCost = calcMonthlyPremiumCost(params.price, params.billingMode);
  if (!(monthlyCost > 0)) return null;

  try {
    const useAppStore = (await import('../store/index')).default;
    const useAdminStore = (await import('../store/adminStore')).default;

    const state = useAppStore.getState();
    const clientData = (state.clientData || {}) as Record<string, any>;
    const bwaData = (clientData.bwaData || {}) as Record<string, any>;
    const zeitraum = Number(bwaData.zeitraum) > 0 ? Number(bwaData.zeitraum) : 12;

    const yearlyDelta = Number((monthlyCost * zeitraum).toFixed(2));
    const prevYearly = Number(bwaData.premiumSoftware) || 0;
    const activatedMap =
      clientData.premiumToolActivatedAt && typeof clientData.premiumToolActivatedAt === 'object'
        ? (clientData.premiumToolActivatedAt as Record<string, string>)
        : {};

    // Bereits gebucht → keine doppelte Kostenbuchung
    if (activatedMap[params.toolId]) {
      return { monthlyCost, yearlyDelta: 0 };
    }

    const nextYearly = Number((prevYearly + yearlyDelta).toFixed(2));
    const activatedAt = new Date().toISOString();
    const planType = params.planType || (params.billingMode === 'yearly' ? 'yearly' : 'monthly');

    const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
    if (!uid) {
      console.error('registerPremiumToolBooking: kein clientId — Abbruch (keine Firebase-Buchung)');
      return null;
    }

    // Zuerst Firebase (Akte) — sonst geht die Laufzeit beim nächsten Reload verloren
    await patchKalkulationMandantRoot(uid, {
      [`clientData.bwaData.premiumSoftware`]: nextYearly,
      [`clientData.premiumToolActivatedAt.${params.toolId}`]: activatedAt,
      [`clientData.premiumToolPlanType.${params.toolId}`]: planType,
    });

    await writeChangeRadarTicket({
      uid,
      clientData,
      title: `Laufende Kosten: ${params.toolName}`,
      message: `Aktion erforderlich: Neue Softwarekosten gebucht (${monthlyCost} €/mtl.) – Lohnfaktorberechnung & Gemeinkostensatz abgleichen.`,
      toolId: params.toolId,
      toolName: params.toolName,
      monthlyCost,
      category: 'Kosten-Update',
    });

    if (typeof state.setClientData === 'function') {
      state.setClientData((prev: any) => ({
        ...(prev || {}),
        bwaData: {
          ...((prev && prev.bwaData) || {}),
          premiumSoftware: nextYearly,
        },
        premiumToolActivatedAt: {
          ...((prev && prev.premiumToolActivatedAt) || {}),
          [params.toolId]: activatedAt,
        },
        premiumToolPlanType: {
          ...((prev && prev.premiumToolPlanType) || {}),
          [params.toolId]: planType,
        },
      }));
    }

    return { monthlyCost, yearlyDelta };
  } catch (err) {
    console.error('registerPremiumToolBooking fehlgeschlagen:', err);
    return null;
  }
};

/**
 * Kündigt Tool-Abo: BWA-Kosten zurücknehmen + Change-Radar-Ticket für den Coach.
 */
export const registerPremiumToolCancellation = async (
  toolId: string
): Promise<{ monthlyCost: number } | null> => {
  const catalog = PREMIUM_TOOL_BOOKING_CATALOG[toolId];
  if (!catalog) return null;

  const monthlyCost = calcMonthlyPremiumCost(catalog.price, catalog.billingMode);

  try {
    const useAppStore = (await import('../store/index')).default;
    const useAdminStore = (await import('../store/adminStore')).default;

    const state = useAppStore.getState();
    const clientData = (state.clientData || {}) as Record<string, any>;
    const bwaData = (clientData.bwaData || {}) as Record<string, any>;
    const zeitraum = Number(bwaData.zeitraum) > 0 ? Number(bwaData.zeitraum) : 12;
    const yearlyDelta = Number((monthlyCost * zeitraum).toFixed(2));
    const prevYearly = Number(bwaData.premiumSoftware) || 0;
    const nextYearly = Math.max(0, Number((prevYearly - yearlyDelta).toFixed(2)));

    const nextActivated = {
      ...(clientData.premiumToolActivatedAt && typeof clientData.premiumToolActivatedAt === 'object'
        ? clientData.premiumToolActivatedAt
        : {}),
    } as Record<string, string>;
    delete nextActivated[toolId];

    if (typeof state.setClientData === 'function') {
      state.setClientData((prev: any) => {
        const activated = {
          ...((prev && prev.premiumToolActivatedAt) || {}),
        };
        delete activated[toolId];
        const planTypes = {
          ...((prev && prev.premiumToolPlanType) || {}),
        };
        delete planTypes[toolId];
        return {
          ...(prev || {}),
          bwaData: {
            ...((prev && prev.bwaData) || {}),
            premiumSoftware: nextYearly,
          },
          premiumToolActivatedAt: activated,
          premiumToolPlanType: planTypes,
        };
      });
    }

    const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
    if (!uid) {
      console.warn('registerPremiumToolCancellation: kein clientId — Radar-Ticket übersprungen');
      return { monthlyCost };
    }

    const nextPlanTypes = {
      ...(clientData.premiumToolPlanType && typeof clientData.premiumToolPlanType === 'object'
        ? clientData.premiumToolPlanType
        : {}),
    } as Record<string, string>;
    delete nextPlanTypes[toolId];

    await writeChangeRadarTicket({
      uid,
      clientData,
      title: `Abo gekündigt: ${catalog.toolName}`,
      message: `Aktion erforderlich: Software-Abo gekündigt (${catalog.toolName}, ${monthlyCost} €/mtl. entfallen) – Lohnfaktorberechnung & Gemeinkostensatz abgleichen.`,
      toolId,
      toolName: catalog.toolName,
      monthlyCost,
      category: 'Abo-Kündigung',
    });

    await patchKalkulationMandantRoot(uid, {
      'clientData.bwaData.premiumSoftware': nextYearly,
      'clientData.premiumToolActivatedAt': nextActivated,
      'clientData.premiumToolPlanType': nextPlanTypes,
    });

    return { monthlyCost };
  } catch (err) {
    console.error('registerPremiumToolCancellation fehlgeschlagen:', err);
    return null;
  }
};
